/**
 * Types for the client-side pushup detection engine.
 *
 * IMPORTANT: All pose detection runs entirely in the client (browser / device)
 * via MediaPipe. Raw video frames are never sent to the backend — the backend
 * only receives structured results (e.g. saved workouts) that the user chooses
 * to sync.
 */

/** ML Kit / MediaPipe pose landmark ids relevant to pushups (BlazePose 33). */
export enum LandmarkId {
  Nose = 0,
  LeftShoulder = 11,
  RightShoulder = 12,
  LeftElbow = 13,
  RightElbow = 14,
  LeftWrist = 15,
  RightWrist = 16,
  LeftHip = 23,
  RightHip = 24,
  LeftKnee = 25,
  RightKnee = 26,
  LeftAnkle = 27,
  RightAnkle = 28,
}

/** A single landmark in normalized image coordinates (0..1) + visibility. */
export interface Point {
  x: number;
  y: number;
  visibility: number;
}

/** One frame of pose data fed into the engine. */
export interface PoseFrame {
  /** Milliseconds timestamp (e.g. performance.now()). */
  timestamp: number;
  /** Only the landmarks we care about; missing landmarks are absent. */
  landmarks: Partial<Record<LandmarkId, Point>>;
}

/**
 * Rep lifecycle state machine. Mirrors the behaviour extracted from the
 * decompiled reference app:
 *
 *   waitingForUp → up → descent → down → ascent → up
 *
 * The state is reset to `waitingForUp` when no pose is seen for a while.
 */
export type RepState = "waitingForUp" | "up" | "descent" | "down" | "ascent";

export type RepQuality = "deep" | "shallow";

/** Events emitted by the engine while processing frames. */
export type EngineEvent =
  | { type: "rep"; rep: number; quality: RepQuality; message?: string }
  | { type: "form-error"; code: string; message: string }
  | { type: "state"; state: RepState };

export interface PushupEngineConfig {
  /** Full = toes on the floor; Knee = knees on the floor. */
  mode: "full" | "knee";
  /**
   * Minimum elbow bend required to count as the bottom of a rep.
   * Extracted thresholds: full = 92°, knee = 108°.
   */
  elbowDepthThresholdDeg?: number;
  /** Stop considering the bottom "held" under this depth improvement. */
  depthHysteresisDeg?: number;
  /** Reset to waitingForUp if no pose is seen for this long (ms). */
  noPoseTimeoutMs?: number;
}
